import React, { useState, useEffect } from "react"
import { db } from "../../../firebaseClient"
import { collection, getCountFromServer, query, where, orderBy, limit, onSnapshot } from "firebase/firestore"
import { getOrganization, suspendOrganization, reinstateOrganization } from "../../../utils/orgHelpers"
import Icon from "../../../components/AppIcon"

const formatEventType = (type) => {
  return (type || "").split("_").map((w) => w.charAt(0) + w.slice(1).toLowerCase()).join(" ")
}

const formatTimestamp = (ts) => {
  const ms = ts?.toMillis ? ts.toMillis() : null
  return ms ? new Date(ms).toLocaleString() : ""
}

const OrganizationDetail = ({ orgId, onBack }) => {
  const [organization, setOrganization] = useState(null)
  const [loading, setLoading] = useState(true)
  const [counts, setCounts] = useState({ courses: null, members: null, certificates: null })
  const [events, setEvents] = useState([])
  const [actionLoading, setActionLoading] = useState(false)

  useEffect(() => {
    const load = async () => {
      const { organization: org } = await getOrganization(orgId)
      setOrganization(org)
      setLoading(false)

      const [coursesSnap, membersSnap, certsSnap] = await Promise.all([
        getCountFromServer(collection(db, "organizations", orgId, "courses")),
        getCountFromServer(collection(db, "organizations", orgId, "members")),
        getCountFromServer(query(collection(db, "certificates"), where("orgId", "==", orgId))),
      ])
      setCounts({
        courses: coursesSnap.data().count,
        members: membersSnap.data().count,
        certificates: certsSnap.data().count,
      })
    }
    load()
  }, [orgId])

  useEffect(() => {
    const q = query(
      collection(db, "events"),
      where("orgId", "==", orgId),
      orderBy("createdAt", "desc"),
      limit(15)
    )
    const unsubscribe = onSnapshot(q, (snap) => {
      setEvents(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    })
    return () => unsubscribe()
  }, [orgId])

  const handleSuspend = async () => {
    const reason = window.prompt("Reason for suspension:")
    if (reason === null) return
    setActionLoading(true)
    await suspendOrganization(orgId, { reason })
    const { organization: org } = await getOrganization(orgId)
    setOrganization(org)
    setActionLoading(false)
  }

  const handleReinstate = async () => {
    setActionLoading(true)
    await reinstateOrganization(orgId)
    const { organization: org } = await getOrganization(orgId)
    setOrganization(org)
    setActionLoading(false)
  }

  if (loading) {
    return <div className="p-6 text-center text-muted-foreground text-sm">Loading organization...</div>
  }

  if (!organization) {
    return (
      <div className="p-6 text-center text-muted-foreground text-sm">
        Organization not found.
        <button onClick={onBack} className="block mx-auto mt-3 text-sm text-primary hover:underline">
          ← Back to Organizations
        </button>
      </div>
    )
  }

  const isSuspended = organization?.status === "suspended"

  return (
    <div className="space-y-6">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <Icon name="ArrowLeft" size={14} />
        Back to Organizations
      </button>

      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            {organization?.logoUrl ? (
              <img src={organization.logoUrl} alt={organization?.name} className="size-12 rounded-lg object-cover" />
            ) : (
              <div className="size-12 rounded-lg bg-muted" />
            )}
            <div>
              <h2 className="text-lg font-bold text-foreground">{organization?.name || "Unnamed Organization"}</h2>
              <p className="text-sm text-muted-foreground">{organization?.orgType || "—"} · {organization?.nesolearnId || organization?.orgId}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className={`text-xs px-2 py-1 rounded-full ${
              isSuspended ? "bg-destructive/10 text-destructive" : "bg-success/10 text-success"
            }`}>
              {isSuspended ? "Suspended" : "Active"}
            </span>
            {isSuspended ? (
              <button
                onClick={handleReinstate}
                disabled={actionLoading}
                className="text-sm px-3 py-1.5 rounded-lg bg-success text-success-foreground hover:opacity-90 disabled:opacity-50"
              >
                Reinstate
              </button>
            ) : (
              <button
                onClick={handleSuspend}
                disabled={actionLoading}
                className="text-sm px-3 py-1.5 rounded-lg bg-destructive text-destructive-foreground hover:opacity-90 disabled:opacity-50"
              >
                Suspend
              </button>
            )}
          </div>
        </div>

        {isSuspended && organization?.suspensionReason && (
          <p className="text-sm text-destructive mt-3 bg-destructive/5 rounded-lg px-3 py-2">
            Suspension reason: {organization.suspensionReason}
          </p>
        )}

        <div className="grid grid-cols-3 gap-4 mt-5 pt-5 border-t border-border">
          <div>
            <p className="text-xs text-muted-foreground">Team Members</p>
            <p className="text-xl font-bold text-foreground">{counts.members ?? "—"}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Courses</p>
            <p className="text-xl font-bold text-foreground">{counts.courses ?? "—"}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Credentials Issued</p>
            <p className="text-xl font-bold text-foreground">{counts.certificates ?? "—"}</p>
          </div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <h3 className="font-semibold text-foreground">Recent Activity</h3>
        </div>
        {events?.length === 0 ? (
          <div className="p-6 text-center text-muted-foreground text-sm">No recorded activity yet.</div>
        ) : (
          <div className="divide-y divide-border">
            {events?.map((event) => (
              <div key={event?.id} className="px-4 py-3 flex items-center justify-between gap-3">
                <span className="text-sm text-foreground">{formatEventType(event?.type)}</span>
                <span className="text-xs text-muted-foreground shrink-0">{formatTimestamp(event?.createdAt)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default OrganizationDetail